import { ApiClient } from "@/api/shared/api-client";
import { enqueueOrganizationLookup } from "@/features/credentials/lib/enqueue-organization-lookup";
import type { Competition, Entity, Season, Venue } from "@/features/credentials/models/organization-resource";
import type { ApiResponse } from "@/models/api-response";

const DEFAULT_LOOKUP_LIMIT = 200;

export class OrganizationResourcesClient extends ApiClient {
  public getCompetitions({
    productToken,
    sport,
    organizationId,
    nameContains,
    limit = DEFAULT_LOOKUP_LIMIT,
  }: {
    productToken: string;
    sport: string;
    organizationId: string;
    nameContains?: string;
    limit?: number;
  }): Promise<ApiResponse<Competition[]>> {
    return enqueueOrganizationLookup(() =>
      this.get<Competition[]>({
        url: `/v1/${sport}/organizations/${organizationId}/competitions`,
        params: { limit, nameContains },
        headers: {
          Authorization: `Bearer ${productToken}`,
        },
      }),
    );
  }

  public getSeasons({
    productToken,
    sport,
    organizationId,
    competitionId,
    nameContains,
    limit = DEFAULT_LOOKUP_LIMIT,
  }: {
    productToken: string;
    sport: string;
    organizationId: string;
    competitionId?: string;
    nameContains?: string;
    limit?: number;
  }): Promise<ApiResponse<Season[]>> {
    return enqueueOrganizationLookup(() =>
      this.get<Season[]>({
        url: `/v1/${sport}/organizations/${organizationId}/seasons`,
        params: { limit, nameContains, competitionId },
        headers: {
          Authorization: `Bearer ${productToken}`,
        },
      }),
    );
  }

  public getEntities({
    productToken,
    sport,
    organizationId,
    seasonId,
    entityId,
    nameContains,
    limit = DEFAULT_LOOKUP_LIMIT,
  }: {
    productToken: string;
    sport: string;
    organizationId: string;
    seasonId?: string;
    entityId?: string;
    nameContains?: string;
    limit?: number;
  }): Promise<ApiResponse<Entity[]>> {
    return enqueueOrganizationLookup(() =>
      this.get<Entity[]>({
        url: `/v1/${sport}/organizations/${organizationId}/entities`,
        params: { limit, nameContains, seasonId, entityId },
        headers: {
          Authorization: `Bearer ${productToken}`,
        },
      }),
    );
  }

  public getVenues({
    productToken,
    sport,
    organizationId,
    nameContains,
    limit = DEFAULT_LOOKUP_LIMIT,
  }: {
    productToken: string;
    sport: string;
    organizationId: string;
    nameContains?: string;
    limit?: number;
  }): Promise<ApiResponse<Venue[]>> {
    return enqueueOrganizationLookup(() =>
      this.get<Venue[]>({
        url: `/v1/${sport}/organizations/${organizationId}/venues`,
        params: { limit, nameContains },
        headers: {
          Authorization: `Bearer ${productToken}`,
        },
      }),
    );
  }
}
